import { isAuthenticated } from '../utils/authHelper';
import React, { Component } from 'react';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import { browserHistory } from 'react-router';

import {
  getAllRecoverableAmountMappings,
  createRecoverableAmountMapping,
  updateRecoverableAmountMapping,
  deleteRecoverableAmountMapping,
  createDateRangeMapping,
  updateDateRangeMapping,
  deleteDateRangeMapping,
  getLedgerNames,
  getAllCostCenters
} from '../utils/PaymentRequestApiUtils';
import Header from './Header';
import userdata from '../config/jwt_token';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar
} from '@material-ui/core';
import CircularProgress from 'material-ui/CircularProgress';

import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

// ─── Searchable single-select dropdown ───────────────────────────────────────
const ssStyles = {
  wrap: { position: 'relative', marginTop: 8, marginBottom: 4 },
  label: { fontSize: 12, color: 'rgba(0,0,0,0.54)', marginBottom: 4, display: 'block' },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid rgba(0,0,0,0.23)',
    borderRadius: 4,
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    background: '#fff',
    cursor: 'text',
  },
  inputDisabled: {
    background: 'rgba(0,0,0,0.06)',
    cursor: 'not-allowed',
    color: 'rgba(0,0,0,0.38)',
  },
  backdrop: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 9999,
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.23)',
    borderRadius: 4,
    marginTop: 2,
    maxHeight: 220,
    overflowY: 'auto',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  option: { padding: '10px 12px', cursor: 'pointer', fontSize: 14 },
  optionHovered: { background: 'rgba(25,118,210,0.08)' },
  optionSelected: { background: 'rgba(25,118,210,0.15)', fontWeight: 600 },
  empty: { padding: '10px 12px', color: 'rgba(0,0,0,0.45)', fontSize: 13 },
};

class SearchableSelect extends Component {
  constructor(props) {
    super(props);
    this.state = { open: false, query: '', hoveredId: null };
    this.inputRef = React.createRef();
  }

  getQuery() {
    const { open, query } = this.state;
    if (open) return query;
    const { options, value } = this.props;
    if (!value && value !== 0) return '';
    const opt = (options || []).find(o => String(o.id) === String(value));
    return opt ? opt.label : '';
  }

  getFiltered() {
    const { options } = this.props;
    const q = this.state.query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter(o => o.label.toLowerCase().includes(q));
  }

  open() {
    if (this.props.disabled) return;
    this.setState({ open: true, query: '' });
  }

  close() {
    this.setState({ open: false, query: '', hoveredId: null });
  }

  select(opt) {
    this.props.onChange(opt.id);
    this.close();
  }

  render() {
    const { label, disabled, placeholder, value } = this.props;
    const { open, hoveredId } = this.state;
    const filtered = this.getFiltered();
    const inputStyle = Object.assign({}, ssStyles.input, disabled ? ssStyles.inputDisabled : {});

    return (
      <div style={ssStyles.wrap}>
        {label && <span style={ssStyles.label}>{label}</span>}
        <input
          ref={this.inputRef}
          style={inputStyle}
          value={this.getQuery()}
          placeholder={placeholder || 'Search...'}
          disabled={disabled}
          onFocus={() => this.open()}
          onChange={e => this.setState({ query: e.target.value, open: true })}
        />
        {open && !disabled && (
          <React.Fragment>
            <div style={ssStyles.backdrop} onClick={() => this.close()} />
            <div style={ssStyles.dropdown}>
              {filtered.length === 0 ? (
                <div style={ssStyles.empty}>No matches</div>
              ) : (
                filtered.map(opt => {
                  const isSelected = String(opt.id) === String(value);
                  const isHovered = String(opt.id) === String(hoveredId);
                  const optStyle = Object.assign(
                    {},
                    ssStyles.option,
                    isSelected ? ssStyles.optionSelected : {},
                    isHovered && !isSelected ? ssStyles.optionHovered : {}
                  );
                  return (
                    <div
                      key={opt.id}
                      style={optStyle}
                      onMouseEnter={() => this.setState({ hoveredId: opt.id })}
                      onMouseLeave={() => this.setState({ hoveredId: null })}
                      onClick={() => this.select(opt)}
                    >
                      {opt.label}
                    </div>
                  );
                })
              )}
            </div>
          </React.Fragment>
        )}
      </div>
    );
  }
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── Grouped-table styles ─────────────────────────────────────────────────────
const tStyles = {
  tableWrap: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    borderRadius: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    tableLayout: 'fixed',
  },
  thead: {
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: '#fff',
  },
  th: {
    padding: '13px 16px',
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    textAlign: 'left',
    borderBottom: '2px solid rgba(255,255,255,0.15)',
    whiteSpace: 'nowrap',
  },
  thCenter: {
    textAlign: 'center',
  },
  // Parent (group) row
  parentRow: {
    background: '#f0f4fa',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background 0.15s',
    borderTop: '2px solid #c8d8f0',
    borderBottom: '1px solid #dde6f5',
  },
  parentRowExpanded: {
    background: '#e8f0fc',
    borderTop: '2px solid #90b4e8',
    borderBottom: '1px solid #c0d4f0',
  },
  parentRowHover: {
    background: '#d8e8f8',
  },
  // Sub (child) row
  subRow: {
    background: '#fff',
    borderBottom: '1px solid #eef1f7',
    transition: 'background 0.12s',
  },
  subRowHover: {
    background: '#f4f7ff',
  },
  subRowLast: {
    borderBottom: '1px solid #eef1f7',
  },
  td: {
    padding: '11px 16px',
    verticalAlign: 'middle',
    color: '#334155',
  },
  tdCenter: {
    textAlign: 'center',
  },
  // Chevron
  chevron: {
    display: 'inline-block',
    marginRight: 10,
    fontSize: 10,
    color: '#1976d2',
    transition: 'transform 0.22s ease',
    fontWeight: 900,
    flexShrink: 0,
  },
  // Property code badge — truncates long IDs
  codeBadge: {
    display: 'inline-block',
    background: '#1976d2',
    color: '#fff',
    borderRadius: 5,
    padding: '3px 9px',
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: '0.02em',
    maxWidth: 110,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  // Ledger count chip
  countChip: {
    display: 'inline-block',
    background: 'rgba(25,118,210,0.10)',
    color: '#1565c0',
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: 11,
    fontWeight: 600,
    border: '1px solid rgba(25,118,210,0.22)',
    letterSpacing: '0.01em',
  },
  ledgerName: {
    color: '#1e3a5f',
    fontWeight: 500,
    fontSize: 13,
  },
  pct: {
    display: 'inline-block',
    background: 'rgba(22,163,74,0.09)',
    color: '#166534',
    borderRadius: 5,
    padding: '3px 10px',
    fontWeight: 600,
    fontSize: 12,
    border: '1px solid rgba(22,163,74,0.18)',
    minWidth: 52,
    textAlign: 'center',
  },
  noData: {
    textAlign: 'center',
    padding: '56px 20px',
    color: '#94a3b8',
    fontSize: 14,
  },
  searchWrap: {
    marginBottom: 18,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  searchInput: {
    flex: 1,
    padding: '10px 16px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 8,
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
    color: '#334155',
    background: '#fff',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  expandAllBtn: {
    padding: '9px 16px',
    fontSize: 12,
    borderRadius: 7,
    border: '1.5px solid #e2e8f0',
    background: '#fff',
    cursor: 'pointer',
    color: '#475569',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
};
// ─────────────────────────────────────────────────────────────────────────────

class RecoverableAmountConfig extends Component {
  constructor(props) {
    super(props);
    if (!isAuthenticated()) {
      browserHistory.push('/');
    }
    this.state = {
      mappings: [],
      ledgerNames: [],
      centers: [],
      loading: false,

      // Grouping UI state
      expandedCenters: {},   // { center_id: true/false }
      filterText: '',
      hoveredParent: null,
      hoveredSubRow: null,

      addEditDialogOpen: false,
      deleteDialogOpen: false,
      isEditMode: false,

      formData: {
        id: null,
        ledger_id: '',
        center_id: '',
        recoverable_amount: ''
      },
      selectedMapping: null,

      // Date-range dialog state
      dateRangeDialogOpen: false,
      dateRangeEditMode: false,
      dateRangeFormData: {
        id: null,
        ledger_id: '',
        center_id: '',
        recoverable_amount: '',
        start_date: '',
        end_date: '',
        reason: '',
        proof_url: '',
        ledger_name: '',
        center_label: ''
      },
      deleteDateRangeDialogOpen: false,
      selectedDateRange: null,

      snackOpen: false,
      snackText: '',

      role: userdata ? userdata.role : ''
    };
  }

  componentDidMount() {
    this.fetchInitialData();
  }

  fetchInitialData = async () => {
    this.setState({ loading: true });
    try {
      await Promise.all([
        this.fetchMappings(),
        this.fetchLedgerNames(),
        this.fetchCenters()
      ]);
    } catch (err) {
      console.error('Error fetching initial data', err);
      this.showSnack('Error loading data');
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchMappings = async () => {
    const res = await getAllRecoverableAmountMappings();
    if (res && res.status === 'success') {
      this.setState({ mappings: res.data || [] });
    }
  };

  fetchLedgerNames = async () => {
    const res = await getLedgerNames();
    if (res && res.status === 'success') {
      this.setState({ ledgerNames: res.data || [] });
    }
  };

  fetchCenters = async () => {
    const res = await getAllCostCenters();
    if (res && res.status === 'success') {
      const centerList = res.data && res.data.cost_centers ? res.data.cost_centers : [];
      this.setState({ centers: centerList });
    }
  };

  showSnack = (message) => {
    this.setState({ snackOpen: true, snackText: message });
  };

  handleCloseSnack = () => {
    this.setState({ snackOpen: false });
  };

  openAddDialog = () => {
    this.setState({
      addEditDialogOpen: true,
      isEditMode: false,
      formData: { id: null, ledger_id: '', center_id: '', recoverable_amount: '' }
    });
  };

  openEditDialog = (mapping) => {
    this.setState({
      addEditDialogOpen: true,
      isEditMode: true,
      formData: {
        id: mapping.id,
        ledger_id: mapping.ledger_id,
        center_id: mapping.center_id,
        recoverable_amount: parseFloat((mapping.recoverable_amount * 100).toPrecision(10))
      }
    });
  };

  openDeleteDialog = (mapping) => {
    this.setState({ deleteDialogOpen: true, selectedMapping: mapping });
  };

  handleFormChange = (field, value) => {
    this.setState(prevState => ({
      formData: { ...prevState.formData, [field]: value }
    }));
  };

  handleSubmit = async () => {
    const { formData, isEditMode } = this.state;

    if (!isEditMode && (!formData.ledger_id || !formData.center_id)) {
      this.showSnack('Please select a ledger and a center');
      return;
    }

    const percentage = parseFloat(formData.recoverable_amount);
    if (formData.recoverable_amount === '' || isNaN(percentage) || percentage < 0 || percentage > 100) {
      this.showSnack('Recoverable amount must be a number between 0 and 100');
      return;
    }

    const amountDecimal = parseFloat((percentage / 100).toPrecision(10));

    this.setState({ loading: true });
    try {
      let res;
      if (isEditMode) {
        res = await updateRecoverableAmountMapping(formData.id, { recoverable_amount: amountDecimal });
      } else {
        res = await createRecoverableAmountMapping({
          ledger_id: formData.ledger_id,
          center_id: formData.center_id,
          recoverable_amount: amountDecimal
        });
      }

      if (res && res.status === 'success') {
        this.setState({ addEditDialogOpen: false, loading: false });
        await this.fetchMappings();
        this.showSnack(isEditMode ? 'Mapping updated successfully' : 'Mapping created successfully');
      } else {
        this.showSnack((res && res.message) || 'Operation failed');
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.showSnack('An error occurred');
      this.setState({ loading: false });
    }
  };

  handleDelete = async () => {
    const { selectedMapping } = this.state;
    this.setState({ loading: true });
    try {
      const res = await deleteRecoverableAmountMapping(selectedMapping.id);
      if (res && res.status === 'success') {
        this.setState({ deleteDialogOpen: false, selectedMapping: null, loading: false });
        await this.fetchMappings();
        this.showSnack('Mapping deleted successfully');
      } else {
        this.showSnack((res && res.message) || 'Delete failed');
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.showSnack('An error occurred');
      this.setState({ loading: false });
    }
  };

  // ─── Date-range CRUD ──────────────────────────────────────────────────────

  openAddDateRangeDialog = (ledger) => {
    this.setState({
      dateRangeDialogOpen: true,
      dateRangeEditMode: false,
      dateRangeFormData: {
        id: null,
        ledger_id: ledger.ledger_id,
        center_id: ledger.center_id,
        recoverable_amount: '',
        start_date: '',
        end_date: '',
        reason: '',
        proof_url: '',
        ledger_name: ledger.ledger_name,
        center_label: (ledger.center_zolocode || '') + ' — ' + (ledger.center_localname || '')
      }
    });
  };

  openEditDateRangeDialog = (dr, ledger) => {
    this.setState({
      dateRangeDialogOpen: true,
      dateRangeEditMode: true,
      dateRangeFormData: {
        id: dr.id,
        ledger_id: ledger.ledger_id,
        center_id: ledger.center_id,
        recoverable_amount: parseFloat((dr.recoverable_amount * 100).toPrecision(10)),
        start_date: dr.start_date,
        end_date: dr.end_date,
        reason: dr.reason || '',
        proof_url: dr.proof_url || '',
        ledger_name: ledger.ledger_name,
        center_label: (ledger.center_zolocode || '') + ' — ' + (ledger.center_localname || '')
      }
    });
  };

  openDeleteDateRangeDialog = (dr) => {
    this.setState({ deleteDateRangeDialogOpen: true, selectedDateRange: dr });
  };

  handleDateRangeFormChange = (field, value) => {
    this.setState(prevState => ({
      dateRangeFormData: { ...prevState.dateRangeFormData, [field]: value }
    }));
  };

  handleDateRangeSubmit = async () => {
    const { dateRangeFormData, dateRangeEditMode } = this.state;
    const { start_date, end_date } = dateRangeFormData;

    if (!start_date || !end_date) {
      this.showSnack('Both start date and end date are required');
      return;
    }
    if (start_date > end_date) {
      this.showSnack('Start date must be before or equal to end date');
      return;
    }

    const percentage = parseFloat(dateRangeFormData.recoverable_amount);
    if (dateRangeFormData.recoverable_amount === '' || isNaN(percentage) || percentage < 0 || percentage > 100) {
      this.showSnack('Recoverable amount must be between 0 and 100');
      return;
    }

    if (!dateRangeFormData.reason || !dateRangeFormData.reason.trim()) {
      this.showSnack('Reason is required for adding/editing a date range override');
      return;
    }

    const amountDecimal = parseFloat((percentage / 100).toPrecision(10));

    this.setState({ loading: true });
    try {
      let res;
      if (dateRangeEditMode) {
        res = await updateDateRangeMapping(dateRangeFormData.id, {
          recoverable_amount: amountDecimal,
          start_date: start_date,
          end_date: end_date,
          reason: dateRangeFormData.reason.trim(),
          proof_url: dateRangeFormData.proof_url || ''
        });
      } else {
        res = await createDateRangeMapping({
          ledger_id: dateRangeFormData.ledger_id,
          center_id: dateRangeFormData.center_id,
          recoverable_amount: amountDecimal,
          start_date: start_date,
          end_date: end_date,
          reason: dateRangeFormData.reason.trim(),
          proof_url: dateRangeFormData.proof_url || ''
        });
      }

      if (res && res.status === 'success') {
        this.setState({ dateRangeDialogOpen: false, loading: false });
        await this.fetchMappings();
        this.showSnack(dateRangeEditMode ? 'Date range updated' : 'Date range created');
      } else {
        this.showSnack((res && res.message) || 'Operation failed');
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.showSnack('An error occurred');
      this.setState({ loading: false });
    }
  };

  handleDeleteDateRange = async () => {
    const { selectedDateRange } = this.state;
    this.setState({ loading: true });
    try {
      const res = await deleteDateRangeMapping(selectedDateRange.id);
      if (res && res.status === 'success') {
        this.setState({ deleteDateRangeDialogOpen: false, selectedDateRange: null, loading: false });
        await this.fetchMappings();
        this.showSnack('Date range deleted');
      } else {
        this.showSnack((res && res.message) || 'Delete failed');
        this.setState({ loading: false });
      }
    } catch (err) {
      console.error(err);
      this.showSnack('An error occurred');
      this.setState({ loading: false });
    }
  };

  // ─── Grouping helpers ───────────────────────────────────────────────────────

  groupMappingsByCenter() {
    const { mappings } = this.state;
    const groups = {};
    mappings.forEach(m => {
      if (!groups[m.center_id]) {
        groups[m.center_id] = {
          center_id: m.center_id,
          center_zolocode: m.center_zolocode,
          center_localname: m.center_localname,
          ledgers: []
        };
      }
      groups[m.center_id].ledgers.push(m);
    });
    // Sort groups by center zolocode
    return Object.values(groups).sort((a, b) =>
      (a.center_zolocode || '').localeCompare(b.center_zolocode || '')
    );
  }

  getFilteredGroups() {
    const { filterText } = this.state;
    const groups = this.groupMappingsByCenter();
    if (!filterText.trim()) return groups;

    const q = filterText.trim().toLowerCase();
    return groups
      .map(g => {
        const centerMatch =
          (g.center_zolocode || '').toLowerCase().includes(q) ||
          (g.center_localname || '').toLowerCase().includes(q);

        const matchedLedgers = g.ledgers.filter(l =>
          (l.ledger_name || '').toLowerCase().includes(q)
        );

        if (centerMatch) return g;               // show all sub-rows if center matches
        if (matchedLedgers.length > 0) {
          return { ...g, ledgers: matchedLedgers }; // show only matching ledger sub-rows
        }
        return null;
      })
      .filter(Boolean);
  }

  toggleExpand = (centerId) => {
    this.setState(prev => ({
      expandedCenters: {
        ...prev.expandedCenters,
        [centerId]: !prev.expandedCenters[centerId]
      }
    }));
  };

  expandAll = () => {
    const groups = this.groupMappingsByCenter();
    const expandedCenters = {};
    groups.forEach(g => { expandedCenters[g.center_id] = true; });
    this.setState({ expandedCenters });
  };

  collapseAll = () => {
    this.setState({ expandedCenters: {} });
  };

  isAnyExpanded() {
    return Object.values(this.state.expandedCenters).some(Boolean);
  }

  // ─── Table render ───────────────────────────────────────────────────────────

  renderGroupedTable() {
    const { expandedCenters, hoveredParent, hoveredSubRow, filterText } = this.state;
    const groups = this.getFilteredGroups();

    return (
      <div>
        {/* Search / filter bar */}
        <div style={tStyles.searchWrap}>
          <input
            style={tStyles.searchInput}
            placeholder="🔍  Search by property code, name, or ledger…"
            value={filterText}
            onChange={e => this.setState({ filterText: e.target.value })}
          />
          <button
            style={tStyles.expandAllBtn}
            onClick={this.isAnyExpanded() ? this.collapseAll : this.expandAll}
          >
            {this.isAnyExpanded() ? '⊟  Collapse All' : '⊞  Expand All'}
          </button>
        </div>

        {/* Summary line */}
        <div style={{ marginBottom: 12, fontSize: 12, color: '#64748b' }}>
          Showing <strong>{groups.length}</strong> propert{groups.length !== 1 ? 'ies' : 'y'} ·{' '}
          <strong>{groups.reduce((s, g) => s + g.ledgers.length, 0)}</strong> ledger mapping{groups.reduce((s, g) => s + g.ledgers.length, 0) !== 1 ? 's' : ''}
        </div>

        <div style={tStyles.tableWrap}>
          <table style={tStyles.table}>
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '38%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '24%' }} />
            </colgroup>
            <thead style={tStyles.thead}>
              <tr>
                <th style={tStyles.th}>Center Code</th>
                <th style={tStyles.th}>Center / Ledger Name</th>
                <th style={{ ...tStyles.th, ...tStyles.thCenter }}>Recoverable %</th>
                <th style={{ ...tStyles.th, ...tStyles.thCenter }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={4} style={tStyles.noData}>
                    {filterText ? 'No results match your search.' : 'No mappings found.'}
                  </td>
                </tr>
              ) : (
                groups.map(group => {
                  const isExpanded = !!expandedCenters[group.center_id];
                  const isHovParent = hoveredParent === group.center_id;

                  const parentRowStyle = Object.assign(
                    {},
                    tStyles.parentRow,
                    isExpanded ? tStyles.parentRowExpanded : {},
                    isHovParent ? tStyles.parentRowHover : {}
                  );

                  return (
                    <React.Fragment key={group.center_id}>
                      {/* ── Parent row ── */}
                      <tr
                        style={parentRowStyle}
                        onClick={() => this.toggleExpand(group.center_id)}
                        onMouseEnter={() => this.setState({ hoveredParent: group.center_id })}
                        onMouseLeave={() => this.setState({ hoveredParent: null })}
                      >
                        {/* Center Code */}
                        <td style={{ ...tStyles.td, fontWeight: 700 }}>
                          <span
                            style={{
                              ...tStyles.chevron,
                              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                              display: 'inline-block',
                            }}
                          >
                            ▶
                          </span>
                          <span style={tStyles.codeBadge}>{group.center_zolocode}</span>
                        </td>

                        {/* Center Name */}
                        <td style={{ ...tStyles.td, color: '#1e3a5f', fontWeight: 600 }}>
                          {group.center_localname}
                        </td>

                        {/* col 3: ledger count chip */}
                        <td style={{ ...tStyles.td, ...tStyles.tdCenter }}>
                          <span style={tStyles.countChip}>
                            {group.ledgers.length} ledger{group.ledgers.length !== 1 ? 's' : ''}
                          </span>
                        </td>

                        {/* col 4: empty */}
                        <td style={tStyles.td} />
                      </tr>

                      {/* ── Sub-rows (ledgers) ── */}
                      {isExpanded && group.ledgers.map((ledger, idx) => {
                        const isLast = idx === group.ledgers.length - 1;
                        const subKey = `${group.center_id}_${ledger.id}`;
                        const isHovSub = hoveredSubRow === subKey;
                        const dateRanges = ledger.date_ranges || [];

                        const subRowStyle = Object.assign(
                          {},
                          tStyles.subRow,
                          isLast && dateRanges.length === 0 ? tStyles.subRowLast : {},
                          isHovSub ? tStyles.subRowHover : {}
                        );

                        return (
                          <React.Fragment key={subKey}>
                            <tr
                              style={subRowStyle}
                              onMouseEnter={() => this.setState({ hoveredSubRow: subKey })}
                              onMouseLeave={() => this.setState({ hoveredSubRow: null })}
                            >
                              {/* col 1: indent mark */}
                              <td style={{
                                ...tStyles.td,
                                paddingLeft: 28,
                                color: '#b0bec5',
                                fontSize: 16,
                                whiteSpace: 'nowrap',
                              }}>
                                └─
                              </td>

                              {/* col 2: ledger name */}
                              <td style={{ ...tStyles.td, ...tStyles.ledgerName }}>
                                {ledger.ledger_name}
                              </td>

                              {/* col 3: recoverable % */}
                              <td style={{ ...tStyles.td, ...tStyles.tdCenter }}>
                                <span style={tStyles.pct}>
                                  {parseFloat((ledger.recoverable_amount * 100).toPrecision(10))}%
                                </span>
                                <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>default</span>
                              </td>

                              {/* col 4: actions */}
                              <td style={{ ...tStyles.td, ...tStyles.tdCenter, whiteSpace: 'nowrap' }}>
                                <Button
                                  size="small"
                                  color="primary"
                                  onClick={(e) => { e.stopPropagation(); this.openAddDateRangeDialog(ledger); }}
                                  style={{ minWidth: 'unset', marginRight: 4, fontSize: 11 }}
                                >
                                  + Range
                                </Button>
                                <Button
                                  size="small"
                                  color="primary"
                                  onClick={(e) => { e.stopPropagation(); this.openEditDialog(ledger); }}
                                  style={{ minWidth: 'unset', marginRight: 4 }}
                                >
                                  <EditIcon fontSize="small" style={{ marginRight: 2 }} />
                                  Edit
                                </Button>
                                <Button
                                  size="small"
                                  color="secondary"
                                  onClick={(e) => { e.stopPropagation(); this.openDeleteDialog(ledger); }}
                                  style={{ minWidth: 'unset' }}
                                >
                                  <DeleteIcon fontSize="small" style={{ marginRight: 2 }} />
                                  Delete
                                </Button>
                              </td>
                            </tr>

                            {/* ── Sub-sub-rows (date ranges) ── */}
                            {dateRanges.map((dr, drIdx) => {
                              const drKey = `${subKey}_dr_${dr.id}`;
                              const isHovDr = hoveredSubRow === drKey;
                              const isLastDr = drIdx === dateRanges.length - 1 && isLast;
                              const drStyle = Object.assign(
                                {},
                                tStyles.subRow,
                                isLastDr ? tStyles.subRowLast : {},
                                isHovDr ? tStyles.subRowHover : {},
                                { background: isHovDr ? '#f0f4ff' : '#f8faff' }
                              );
                              const startLabel = dr.start_date ? new Date(dr.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                              const endLabel = dr.end_date ? new Date(dr.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

                              return (
                                <tr
                                  key={drKey}
                                  style={drStyle}
                                  onMouseEnter={() => this.setState({ hoveredSubRow: drKey })}
                                  onMouseLeave={() => this.setState({ hoveredSubRow: null })}
                                >
                                  {/* col 1: deeper indent */}
                                  <td style={{ ...tStyles.td, paddingLeft: 48, color: '#cbd5e1', fontSize: 14 }}>
                                    └─
                                  </td>

                                  {/* col 2: date range chip + reason/proof */}
                                  <td style={{ ...tStyles.td, fontSize: 12, color: '#475569' }}>
                                    <span style={{
                                      display: 'inline-block',
                                      background: 'rgba(25,118,210,0.06)',
                                      border: '1px solid rgba(25,118,210,0.18)',
                                      borderRadius: 5,
                                      padding: '3px 10px',
                                      fontSize: 11,
                                      fontWeight: 500,
                                      color: '#1565c0',
                                    }}>
                                      📅 {startLabel} → {endLabel}
                                    </span>
                                    {dr.reason && (
                                      <span style={{
                                        display: 'inline-block',
                                        marginLeft: 8,
                                        background: 'rgba(100,116,139,0.08)',
                                        border: '1px solid rgba(100,116,139,0.15)',
                                        borderRadius: 4,
                                        padding: '2px 8px',
                                        fontSize: 10,
                                        color: '#64748b',
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'middle',
                                      }} title={dr.reason}>
                                        📝 {dr.reason}
                                      </span>
                                    )}
                                    {dr.proof_url && (
                                      <a
                                        href={dr.proof_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{
                                          display: 'inline-block',
                                          marginLeft: 6,
                                          background: 'rgba(16,185,129,0.08)',
                                          border: '1px solid rgba(16,185,129,0.2)',
                                          borderRadius: 4,
                                          padding: '2px 8px',
                                          fontSize: 10,
                                          color: '#059669',
                                          textDecoration: 'none',
                                          fontWeight: 500,
                                          verticalAlign: 'middle',
                                        }}
                                      >
                                      📎 Proof
                                      </a>
                                    )}
                                    {dr.created_by && (
                                      <span style={{
                                        display: 'inline-block',
                                        marginLeft: 6,
                                        background: 'rgba(139,92,246,0.08)',
                                        border: '1px solid rgba(139,92,246,0.18)',
                                        borderRadius: 4,
                                        padding: '2px 8px',
                                        fontSize: 10,
                                        color: '#7c3aed',
                                        fontWeight: 500,
                                        verticalAlign: 'middle',
                                      }} title={'Created by: ' + dr.created_by}>
                                        👤 {dr.created_by.split('@')[0]}
                                      </span>
                                    )}
                                  </td>

                                  {/* col 3: % */}
                                  <td style={{ ...tStyles.td, ...tStyles.tdCenter }}>
                                    <span style={{ ...tStyles.pct, background: 'rgba(234,88,12,0.08)', color: '#c2410c', borderColor: 'rgba(234,88,12,0.18)' }}>
                                      {parseFloat((dr.recoverable_amount * 100).toPrecision(10))}%
                                    </span>
                                  </td>

                                  {/* col 4: actions */}
                                  <td style={{ ...tStyles.td, ...tStyles.tdCenter, whiteSpace: 'nowrap' }}>
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={(e) => { e.stopPropagation(); this.openEditDateRangeDialog(dr, ledger); }}
                                      style={{ minWidth: 'unset', marginRight: 4 }}
                                    >
                                      <EditIcon fontSize="small" style={{ marginRight: 2 }} />
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      color="secondary"
                                      onClick={(e) => { e.stopPropagation(); this.openDeleteDateRangeDialog(dr); }}
                                      style={{ minWidth: 'unset' }}
                                    >
                                      <DeleteIcon fontSize="small" style={{ marginRight: 2 }} />
                                      Delete
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const {
      ledgerNames, centers, loading,
      addEditDialogOpen, deleteDialogOpen, isEditMode,
      formData, selectedMapping
    } = this.state;

    const ledgerOptions = ledgerNames.map(l => ({ id: l.id, label: l.value }));
    const centerOptions = centers.map(c => ({
      id: c.Center_ID,
      label: `${c.ZOLOCODE} — ${c.LOCALNAME}`
    }));

    return (
      <MuiThemeProvider>
        <div>
          <Header />
          <div style={{ padding: 20, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <Typography variant="h6">Recoverable Amount Configuration</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={this.openAddDialog}
              >
                <AddIcon style={{ marginRight: 5 }} />
                Add Mapping
              </Button>
            </div>

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                <CircularProgress size={60} thickness={7} />
              </div>
            )}

            {!loading && this.renderGroupedTable()}

            {/* Add / Edit Dialog */}
            <Dialog
              open={addEditDialogOpen}
              onClose={() => this.setState({ addEditDialogOpen: false })}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                {isEditMode ? 'Edit Recoverable Amount' : 'Add Recoverable Amount Mapping'}
              </DialogTitle>
              <DialogContent>
                <SearchableSelect
                  label="Ledger Name"
                  placeholder="Search ledger..."
                  options={ledgerOptions}
                  value={formData.ledger_id}
                  onChange={id => this.handleFormChange('ledger_id', id)}
                  disabled={isEditMode}
                />

                <SearchableSelect
                  label="Center"
                  placeholder="Search by code or name..."
                  options={centerOptions}
                  value={formData.center_id}
                  onChange={id => this.handleFormChange('center_id', id)}
                  disabled={isEditMode}
                />

                <TextField
                  margin="dense"
                  label="Recoverable Amount (%)"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  value={formData.recoverable_amount}
                  onChange={(e) => this.handleFormChange('recoverable_amount', e.target.value)}
                  helperText="Enter percentage between 0 and 100 (e.g. 10 for 10%)"
                  style={{ marginTop: 8 }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => this.setState({ addEditDialogOpen: false })}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button onClick={this.handleSubmit} color="primary" disabled={loading}>
                  {loading
                    ? <CircularProgress size={20} thickness={5} />
                    : (isEditMode ? 'Update' : 'Create')
                  }
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
              open={deleteDialogOpen}
              onClose={() => this.setState({ deleteDialogOpen: false, selectedMapping: null })}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                {selectedMapping && (
                  <Typography>
                    Are you sure you want to delete the mapping for{' '}
                    <strong>{selectedMapping.ledger_name}</strong> —{' '}
                    <strong>{selectedMapping.center_zolocode} ({selectedMapping.center_localname})</strong>?
                    This action cannot be undone.
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => this.setState({ deleteDialogOpen: false, selectedMapping: null })}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button onClick={this.handleDelete} color="secondary" variant="contained" disabled={loading}>
                  {loading
                    ? <CircularProgress size={20} thickness={5} style={{ color: 'white' }} />
                    : 'Delete'
                  }
                </Button>
              </DialogActions>
            </Dialog>

            <Snackbar
              open={this.state.snackOpen}
              autoHideDuration={3000}
              onClose={this.handleCloseSnack}
              message={this.state.snackText}
            />

            {/* Date Range Add/Edit Dialog */}
            <Dialog
              open={this.state.dateRangeDialogOpen}
              onClose={() => this.setState({ dateRangeDialogOpen: false })}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>
                {this.state.dateRangeEditMode ? 'Edit Date Range Override' : 'Add Date Range Override'}
              </DialogTitle>
              <DialogContent>
                <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
                  <strong>Ledger:</strong> {this.state.dateRangeFormData.ledger_name}<br />
                  <strong>Center:</strong> {this.state.dateRangeFormData.center_label}
                </div>

                <TextField
                  margin="dense"
                  label="Start Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={this.state.dateRangeFormData.start_date}
                  onChange={(e) => this.handleDateRangeFormChange('start_date', e.target.value)}
                  style={{ marginBottom: 8 }}
                />

                <TextField
                  margin="dense"
                  label="End Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={this.state.dateRangeFormData.end_date}
                  onChange={(e) => this.handleDateRangeFormChange('end_date', e.target.value)}
                  style={{ marginBottom: 8 }}
                />

                <TextField
                  margin="dense"
                  label="Recoverable Amount (%)"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  value={this.state.dateRangeFormData.recoverable_amount}
                  onChange={(e) => this.handleDateRangeFormChange('recoverable_amount', e.target.value)}
                  helperText="Enter percentage between 0 and 100. This overrides the default for invoices within the date range."
                  style={{ marginTop: 8 }}
                />

                <TextField
                  margin="dense"
                  label="Reason *"
                  fullWidth
                  multiline
                  rows={2}
                  value={this.state.dateRangeFormData.reason}
                  onChange={(e) => this.handleDateRangeFormChange('reason', e.target.value)}
                  helperText="Explain why this override is needed (required)"
                  style={{ marginTop: 8 }}
                />

                <TextField
                  margin="dense"
                  label="Proof URL (optional)"
                  fullWidth
                  value={this.state.dateRangeFormData.proof_url}
                  onChange={(e) => this.handleDateRangeFormChange('proof_url', e.target.value)}
                  helperText="Link to supporting document (e.g. agreement, email, approval)"
                  style={{ marginTop: 8 }}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => this.setState({ dateRangeDialogOpen: false })}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button onClick={this.handleDateRangeSubmit} color="primary" disabled={loading}>
                  {loading
                    ? <CircularProgress size={20} thickness={5} />
                    : (this.state.dateRangeEditMode ? 'Update' : 'Create')
                  }
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Date Range Confirmation */}
            <Dialog
              open={this.state.deleteDateRangeDialogOpen}
              onClose={() => this.setState({ deleteDateRangeDialogOpen: false, selectedDateRange: null })}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Confirm Delete Date Range</DialogTitle>
              <DialogContent>
                <Typography>
                  Are you sure you want to delete this date range override?
                  {this.state.selectedDateRange && (
                    <span>
                      <br /><strong>
                        {new Date(this.state.selectedDateRange.start_date).toLocaleDateString('en-IN')} → {new Date(this.state.selectedDateRange.end_date).toLocaleDateString('en-IN')}
                      </strong> at <strong>
                        {parseFloat((this.state.selectedDateRange.recoverable_amount * 100).toPrecision(10))}%
                      </strong>
                    </span>
                  )}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => this.setState({ deleteDateRangeDialogOpen: false, selectedDateRange: null })}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button onClick={this.handleDeleteDateRange} color="secondary" variant="contained" disabled={loading}>
                  {loading
                    ? <CircularProgress size={20} thickness={5} style={{ color: 'white' }} />
                    : 'Delete'
                  }
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        </div>
      </MuiThemeProvider>
    );
  }
}

export default RecoverableAmountConfig;



// WEBPACK FOOTER //
// ./src/components/RecoverableAmountConfig.js